/**
 * UUIDs im Haus, ohne Abhängigkeit.
 *
 * `uuidv5` braucht SHA-1. `crypto.subtle.digest` kann das, aber nur asynchron — und die
 * Stammdatenkennungen werden beim Aufbau der Tabellen synchron gebraucht. SHA-1 ist ein
 * festes, kurzes Stück Rechnung; es hier zu haben ist billiger als jeder Umweg.
 *
 * ⚠️ SHA-1 dient hier ausschließlich der **Namensbildung** nach RFC 4122. Es ist keine
 * Sicherheitsfunktion und darf für keine verwendet werden.
 */

/** Die feste Relay-Seed-Konstante, selbst `uuid5(NAMESPACE_DNS, "seed.schema.metis")`. */
export const RELAY_SEED_NS = 'f6516588-02c3-5444-bbc1-9d3b303f6258';

export function sha1(bytes: Uint8Array): Uint8Array {
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array((((bytes.length + 8) >> 6) + 1) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(padded.length - 4, bitLength >>> 0, false);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Uint32Array(80);
  for (let block = 0; block < padded.length; block += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(block + i * 4, false);
    for (let i = 16; i < 80; i += 1) {
      const mixed = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
      w[i] = ((mixed << 1) | (mixed >>> 31)) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let i = 0; i < 80; i += 1) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const t = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = ((b << 30) | (b >>> 2)) >>> 0;
      b = a;
      a = t;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, h0, false);
  outView.setUint32(4, h1, false);
  outView.setUint32(8, h2, false);
  outView.setUint32(12, h3, false);
  outView.setUint32(16, h4, false);
  return out;
}

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) throw new Error('Keine UUID: ' + uuid);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function format(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return (
    hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) +
    '-' + hex.slice(16, 20) + '-' + hex.slice(20, 32)
  );
}

/** Namensbasierte UUID nach RFC 4122, Fassung 5. Gleiche Eingabe ⇒ gleiche Kennung, überall. */
export function uuidv5(namespace: string, name: string): string {
  const ns = uuidToBytes(namespace);
  const text = new TextEncoder().encode(name);
  const input = new Uint8Array(ns.length + text.length);
  input.set(ns);
  input.set(text, ns.length);

  const hash = sha1(input).slice(0, 16);
  hash[6] = (hash[6] & 0x0f) | 0x50; // Fassung 5
  hash[8] = (hash[8] & 0x3f) | 0x80; // Variante RFC 4122
  return format(hash);
}

/**
 * Zeitgeordnete UUID nach Fassung 7: 48 Bit Millisekunden, danach 74 Bit, die als Zähler
 * geführt werden (RFC 9562, „Monotonic Random", Methode 3) statt bei jedem Aufruf neu gewürfelt
 * zu werden. Reiner Zufall in diesen 74 Bit sortiert innerhalb derselben Millisekunde nicht —
 * zwei Aufrufe in derselben Millisekunde hätten dieselbe Chance, in beliebiger Reihenfolge zu
 * liegen. Der Zähler macht die Kennung **innerhalb des laufenden Prozesses** durchgängig
 * aufsteigend, nicht nur zwischen verschiedenen Millisekunden.
 *
 * Die 74 Bit setzen sich zusammen aus: unterem Nibble von Byte 6 (4 Bit, die oberen 4 Bit
 * tragen die feste Fassungskennung), Byte 7 (8 Bit), unteren 6 Bit von Byte 8 (die oberen 2 Bit
 * tragen die feste RFC-4122-Variante) und den Bytes 9–15 (56 Bit) — macht 4+8+6+56 = 74 Bit.
 * Weil die festen Bits an derselben Stelle jeder Kennung stehen, ändert ihr Vorhandensein die
 * relative Ordnung zweier Zählerwerte nicht: das Einfügen einer Konstante an fester Position
 * verschiebt beide Werte um denselben Betrag.
 *
 * Zwei Randfälle, ausdrücklich entschieden:
 *
 * - **Überlauf des Zählers innerhalb einer Millisekunde**: 2^74 Werte je Millisekunde sind im
 *   Betrieb dieser App unerreichbar (das wären mehr als 10^16 Aufrufe pro Sekunde). Trotzdem:
 *   liefe der Zähler über, wird die Millisekunde künstlich um eins weitergestellt und der
 *   Zähler neu gewürfelt — das erhält die Eindeutigkeit und die Monotonie (der Zeitstempel
 *   steigt, dominiert also die Ordnung), nur dass der emittierte Zeitstempel dann der Wirklichkeit
 *   kurzzeitig vorauseilt statt hinterherzuhinken.
 * - **Uhrensprung rückwärts** (`Date.now()` liefert einen kleineren Wert als beim vorigen
 *   Aufruf, z. B. durch NTP-Korrektur): der kleinere Wert wird verworfen, die zuletzt vergebene
 *   Millisekunde bleibt stehen, und nur der Zähler läuft weiter. Damit bleibt jede Kennung
 *   innerhalb des Prozesses garantiert monoton, auch über einen Rückwärtssprung hinweg — der
 *   eingebettete Zeitstempel weicht dann für die Dauer des Sprungs von der realen Uhrzeit ab,
 *   was für eine Sortierkennung hier wichtiger ist als Uhrzeittreue.
 */
const ZÄHLER_BITS = 74n;
const ZÄHLER_MASKE = (1n << ZÄHLER_BITS) - 1n;

let zuletztMs = -1;
let zuletztZähler = 0n;

function zufallsZähler(): bigint {
  const zufall = new Uint8Array(10); // 80 Bit roh gezogen, unten auf 74 Bit maskiert
  crypto.getRandomValues(zufall);
  let n = 0n;
  for (const byte of zufall) n = (n << 8n) | BigInt(byte);
  return n & ZÄHLER_MASKE;
}

export function uuidv7(): string {
  const now = Date.now();

  if (now > zuletztMs) {
    zuletztMs = now;
    zuletztZähler = zufallsZähler();
  } else {
    // Gleiche Millisekunde oder Uhrensprung rückwärts: `zuletztMs` bleibt stehen, nur der
    // Zähler steigt — siehe Randfall-Kommentar oben.
    zuletztZähler += 1n;
    if (zuletztZähler > ZÄHLER_MASKE) {
      zuletztMs += 1;
      zuletztZähler = zufallsZähler();
    }
  }

  const bytes = new Uint8Array(16);
  const ms = zuletztMs;
  bytes[0] = (ms / 0x10000000000) & 0xff;
  bytes[1] = (ms / 0x100000000) & 0xff;
  bytes[2] = (ms / 0x1000000) & 0xff;
  bytes[3] = (ms / 0x10000) & 0xff;
  bytes[4] = (ms / 0x100) & 0xff;
  bytes[5] = ms & 0xff;

  // 74 Zählerbits von Byte 15 (niedrigstwertig) rückwärts bis Byte 6 (höchstwertig) verteilen.
  let z = zuletztZähler;
  for (let i = 15; i >= 9; i -= 1) {
    bytes[i] = Number(z & 0xffn);
    z >>= 8n;
  }
  bytes[8] = Number(z & 0x3fn);
  z >>= 6n;
  bytes[7] = Number(z & 0xffn);
  z >>= 8n;
  bytes[6] = Number(z & 0x0fn);

  bytes[6] |= 0x70; // Fassung 7
  bytes[8] |= 0x80; // Variante RFC 4122
  return format(bytes);
}
